import {
	Children,
	cloneElement,
	FC,
	isValidElement,
	ReactElement,
} from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";

import Box from "../../layout/boxes/Box";

import {
	StyledCircularStepperCircle,
	StyledCircularStepperProgress,
	StyledCircularStepperSteps,
} from "./styling/CircularStepper.styles";
import {
	CircularStepperProps,
	CircularStepperSubComponents,
} from "./CircularStepper.types";
import CircularStepperStep from "./CircularStepperStep";
import Typography from "../../typography/Typography";
import Icon from "../../imagery/Icon";

const INVALID_CHILD_ERROR_MESSAGE =
	"Invalid CircularStepper subcomponent: The child you provided is not a valid CircularStepper subcomponent, please provide CircularStepper.Step subcomponent";

const CircularStepper: FC<CircularStepperProps> &
	CircularStepperSubComponents = ({
	children,
	className,
	colorOn,
	successLabel = "Success!",
	successText,
	...props
}) => {
	const block = new BemIt("CircularStepper");

	const stepCount = Children.count(children);
	let completedStepCount = 0;

	const steps = Children.map(children, (child, index) => {
		if (isValidElement(child)) {
			const isItem = child.type === CircularStepperStep;

			if (!isItem) {
				console.error(INVALID_CHILD_ERROR_MESSAGE);
				return null;
			}

			const { status } = child.props;

			if (status === "completed") {
				completedStepCount++;
			}

			return (
				isValidElement(child) &&
				cloneElement(child as ReactElement, {
					key: index,
					colorOn,
				})
			);
		}
	});

	const isCompleted = completedStepCount === stepCount;
	const progressLabel = `${completedStepCount + 1}/${stepCount}`;

	return (
		<Box className={addClass(className).after(block)} display="flex" {...props}>
			<StyledCircularStepperProgress>
				<StyledCircularStepperCircle
					aria-labelledby="progress-label"
					completedStepCount={completedStepCount}
					stepCount={stepCount}
					role="figure"
				/>
				{isCompleted ? (
					<Icon
						id="progress-label"
						aria-label="completed"
						name="check"
						size="sm"
					/>
				) : (
					<Typography id="progress-label" weight="heaviest" colorOn={colorOn}>
						{progressLabel}
					</Typography>
				)}
			</StyledCircularStepperProgress>
			<StyledCircularStepperSteps>
				{steps}
				<CircularStepperStep
					status={isCompleted ? "active" : "default"}
					label={successLabel}
					text={successText}
				/>
			</StyledCircularStepperSteps>
		</Box>
	);
};

CircularStepper.Step = CircularStepperStep;

export default CircularStepper;
